/**
 * Main layout: ambient background, floating glass navbar, route transitions,
 * toast notifications.
 */
import React from 'react';
import { useLocation } from 'react-router';
import { AnimatePresence, motion } from 'motion/react';
import Navbar from '../components/Navbar';
import ToastContainer from '../components/ui/ToastContainer';
import AnimatedBackground from '../components/AnimatedBackground';
import { useAppStore } from '../store/appStore';

interface MainLayoutProps {
  children: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const location = useLocation();
  const { toasts, removeToast } = useAppStore();

  // Immersive routes hide the navbar (in-quiz focus mode)
  const hideNavbarPaths = ['/assessment'];
  const shouldHideNavbar = hideNavbarPaths.includes(location.pathname);

  return (
    <div className="relative min-h-screen">
      <AnimatedBackground />
      {!shouldHideNavbar && <Navbar />}

      <main className={shouldHideNavbar ? '' : 'pt-20'}>
        {/* Route switch: the old page gets out fast (no dead half-faded
            beat), the new one settles in on a soft spring. Pages keep their
            own entrance choreography on top. */}
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 14, scale: 0.995 }}
            animate={{
              opacity: 1,
              y: 0,
              scale: 1,
              transition: { type: 'spring', stiffness: 300, damping: 30, mass: 0.9 },
            }}
            exit={{
              opacity: 0,
              y: -8,
              transition: { duration: 0.1, ease: 'easeIn' },
            }}
          >
            {children}
          </motion.div>
        </AnimatePresence>
      </main>

      <ToastContainer toasts={toasts} onClose={removeToast} />
    </div>
  );
};

export default MainLayout;
